import { Nullable } from "common/records";
import { CommissionMethod, EstimateRecord, IEstimateRecord } from "common/records/Estimate";
import { EstimateCommissionRecord } from "common/records/EstimateCommission";
import { EstimateGroupRecord, IEstimateGroupRecord } from "common/records/EstimateGroup";
import { EstimateLineItemRecord, IEstimateLineItemRecord } from "common/records/EstimateLineItem";
import { EstimateFinanceOptionRecord, IEstimateFinanceOptionRecord } from "common/records/EstimateFinanceOption";
import { RootState } from "common/reducers";
import { ids } from "common/selectors/idsManagement.selectors";
import { List, Map } from "immutable";
import { createSelector } from "reselect";

const rootKey = "estimates";

const EMPTY_ESTIMATE_OVERVIEWS = List<EstimateRecord>();

/**
 * Loads byId key from estimates
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, EstimateRecord>} Map<number, EstimateRecord>
 */
export const byId = (state: RootState): Map<number, EstimateRecord> => state.getIn([rootKey, "byId"]);

/**
 * Loads the estimate by id
 *
 * @param {RootState} state The RootState
 * @param props
 * @returns
 */
export const estimate = (state: RootState, props: { estimateId: Nullable<number> }) =>
  props.estimateId ? byId(state).get(props.estimateId) : undefined;

/**
 * Loads currentEstimateId key from estimates
 *
 * @param {RootState} state The RootState
 * @returns {Nullable<number>} number
 */
export const currentEstimateId = (state: RootState): Nullable<number> => state.getIn([rootKey, "currentEstimateId"]);

/** Returns the one-shot line-item id the new-estimate auto-open should open, or null. */
export const autoOpenLineItemId = (state: RootState): number | null =>
  state.getIn([rootKey, "autoOpenLineItemId"]) as number | null;

/**
 * Loads currentEstimate key from estimates
 *
 * @param {RootState} state The RootState
 * @returns
 */
export const currentEstimate = createSelector([byId, currentEstimateId], (byId, estimateId) => {
  if (!estimateId) {
    return null;
  }

  return byId.get(estimateId);
});

/**
 * The current estimate's activated_price_list_id.
 *
 * @param {RootState} state The RootState
 * @returns {number | null} the current estimate's APL id (0 when unset), or null when no current estimate
 */
export const currentEstimateActivatedPriceListId = createSelector(
  [currentEstimate],
  (est): number | null => est?.activated_price_list_id ?? null,
);

/**
 * Loads the current estimate name
 *
 * @param {RootState} state The RootState
 * @returns {string} string
 *
 */
export const currentEstimateName = createSelector([currentEstimate], (est) => est?.get("name") || "");

/**
 * Loads the current estimate loading
 *
 * @param {RootState} state The RootState
 * @returns {boolean} boolean
 */
export const currentEstimateLoading = createSelector([currentEstimate], (est) => est?.get("loading") || false);

/**
 * Loads the current estimate group ids
 *
 * @param {RootState} state The RootState
 * @returns {List<number>} List<number>
 *
 */
export const currentEstimateGroupIds = createSelector(
  [currentEstimate],
  (est) => est?.get("group_ids") || List<number>(),
);

/**
 * Loads the current estimate metadata
 *
 * @param {RootState} state The RootState
 * @returns {Map<string, any>} Map<string, any>
 */
export const currentEstimateMetadata = (state: RootState, props: { metadataName: string }) => {
  const estimateId = currentEstimateId(state);
  return state.getIn(["estimates", "byId", estimateId, "metadata", props.metadataName]);
};

/**
 * Loads currentListingType key from estimates
 *
 * @param {RootState} state The RootState
 * @returns {string} string
 */
export const currentListingType = (state: RootState) => state.getIn([rootKey, "currentListingType"]);

/**
 * Loads estimateDocsLoaded key from estimates
 *
 * @param {RootState} state The RootState
 * @returns {boolean} boolean
 */
export const estimateDocsLoaded = (state: RootState) => state.getIn([rootKey, "estimateDocsLoaded"]);

export const overviewsById = (state: RootState, _props?: any): Map<number, EstimateRecord> =>
  state.getIn([rootKey, "overviewsById"]);

export const estimateOverviews = createSelector(
  [overviewsById, (state: RootState) => ids(state, { modelName: "estimateOverviews" })],
  (overviewsById, overviewIds): List<EstimateRecord> =>
    overviewIds.isEmpty()
      ? EMPTY_ESTIMATE_OVERVIEWS
      : overviewIds
          .map((id) => (typeof id === "number" ? overviewsById.get(id) : undefined))
          .filter((overview): overview is EstimateRecord => !!overview),
);

export const estimateOverview = createSelector(
  [overviewsById, (state: RootState, props: { estimateId: number }) => props],
  (overviewsById, props) => {
    return overviewsById.get(props.estimateId);
  },
);

/**
 * Loads the document ids  using estimate id
 *
 * @param {RootState} state The RootState
 * @param {{ estimateId: number }} options
 * @returns list of document ids
 */
export const estimateDocIds = (state: RootState, props?: any) =>
  state.getIn([rootKey, "estimateDocs"]).get(props.estimateId);

/**
 * Loads commission test data
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {CommissionTestDataRecord} CommissionTestDataRecord
 */
export const commissionTestData = (state: RootState) => state.getIn([rootKey, "commissionTestData"]);

/**
 * The single-slot server-computed commission breakdown for the Commission Calculator (greenline/custom
 * methods). Always a record — reset to an empty record on each modal open (FETCH_ESTIMATE_COMMISSION).
 *
 * @param {RootState} state The RootState
 * @returns {EstimateCommissionRecord} the calculated commission record
 */
export const estimateCommission = (state: RootState): EstimateCommissionRecord =>
  state.getIn([rootKey, "calculatedCommission"]);

/**
 * The copyable "Commission Sheet" summary for the server-computed methods. Custom joins each output
 * object's preformatted `formatted` line; greenline (and any other value) joins the preformatted
 * `verbose_output` lines. Newline-delimited; empty string when nothing has been calculated.
 *
 * @param {RootState} state The RootState
 * @param {string} commissionMethod The org's configured commission method
 * @returns {string} the newline-joined commission-sheet summary
 */
export const estimateCommissionSummary = createSelector(
  [estimateCommission, (_: RootState, commissionMethod = "") => commissionMethod],
  (commission, commissionMethod): string => {
    const output = commission.output;
    const lines =
      commissionMethod === CommissionMethod.custom
        ? output.verbose_output_objects.map((obj) => obj.formatted)
        : output.verbose_output;
    return lines.join("\n");
  },
);

/**
 * Gets the current unsaved ID for creating new estimates
 *
 * @param {RootState} state The RootState
 * @returns {number} The current unsaved ID (negative number)
 */
export const unsavedId = (state: RootState): number => state.getIn([rootKey, "unsavedId"]) as number;

/**
 * Loads lastSavedById key from estimates (represents what's saved on the server)
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, EstimateRecord>} Map<number, EstimateRecord>
 */
export const lastSavedById = (state: RootState) => state.getIn([rootKey, "lastSavedById"]);

/**
 * Loads editingSnapshotById key from estimateLineItems (snapshots for rollback on cancel)
 *
 * @param {RootState} state The RootState
 * @returns {Map} Map of line item snapshots by id
 */
export const lineItemEditingSnapshots = (state: RootState) => state.getIn(["estimateLineItems", "editingSnapshotById"]);

/**
 * Loads editingSnapshotById key from estimates (snapshots for rollback on cancel)
 *
 * @param {RootState} state The RootState
 * @returns {Map} Map of estimate snapshots by id
 */
export const estimateEditingSnapshots = (state: RootState) => state.getIn(["estimates", "editingSnapshotById"]);

/**
 * Checks whether a specific line item has an active editing snapshot.
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemId: number }} props The line item id
 * @returns {boolean} true if a snapshot exists for the given line item
 */
export const hasEditingSnapshot = createSelector(
  [lineItemEditingSnapshots, (_: RootState, props: { estimateLineItemId: number }) => props.estimateLineItemId],
  (snapshots, estimateLineItemId) => !!(snapshots && snapshots.has(estimateLineItemId)),
);

/**
 * Returns true while the currently-open editing session is a brand-new draft (opened via "Add Line
 * Item"), before its first local save. Read from the explicit `newDraftId` session marker set by
 * START_ADDING_LINE_ITEM and cleared on commit/cancel/reset — draft-ness is tracked explicitly
 * rather than inferred from the editing snapshot, which is scoped to the edited line item. Returns
 * false for committed local drafts and server-persisted items.
 *
 * @param {RootState} state
 * @param {{ estimateLineItemId: number }} props
 * @returns {boolean}
 */
export const isNewDraftLineItem = createSelector(
  [
    (state: RootState) => state.getIn(["estimateLineItems", "newDraftId"]) as number | null,
    (_: RootState, props: { estimateLineItemId: number }) => props.estimateLineItemId,
  ],
  (newDraftId, lineItemId) => newDraftId != null && newDraftId === lineItemId,
);

/** @private Input selector: estimateGroups.byId */
const estimateGroupsById = (state: RootState): Map<number, EstimateGroupRecord> =>
  state.getIn(["estimateGroups", "byId"]);

/** @private Input selector: estimateGroups.lastSavedById */
const estimateGroupsLastSavedById = (state: RootState) => state.getIn(["estimateGroups", "lastSavedById"]);

/** @private Input selector: estimateLineItems.byId */
const estimateLineItemsById = (state: RootState): Map<number, EstimateLineItemRecord> =>
  state.getIn(["estimateLineItems", "byId"]);

/** @private Input selector: estimateLineItems.lastSavedById */
const estimateLineItemsLastSavedById = (state: RootState) => state.getIn(["estimateLineItems", "lastSavedById"]);

/** @private Input selector: estimateLineItemOptions.byId */
const estimateLineItemOptionsById = (state: RootState) => state.getIn(["estimateLineItemOptions", "byId"]);

/** @private Input selector: estimateLineItemOptions.lastSavedById */
const estimateLineItemOptionsLastSavedById = (state: RootState) =>
  state.getIn(["estimateLineItemOptions", "lastSavedById"]);

const estimateFinanceOptionsById = (state: RootState): Map<number, EstimateFinanceOptionRecord> =>
  state.getIn(["estimateFinanceOptions", "byId"]);

const estimateFinanceOptionsLastSavedById = (state: RootState): Map<number, EstimateFinanceOptionRecord> =>
  state.getIn(["estimateFinanceOptions", "lastSavedById"]);

/**
 * Count of live (non-`_destroy`) line items across the current estimate's groups. This is how
 * "empty estimate" is measured for template application (rsf-app checks `lineItems().length === 0`),
 * NOT group count.
 *
 * @param {RootState} state The RootState
 * @returns {number} the number of live line items on the current estimate
 */
export const currentEstimateLineItemCount = createSelector(
  [currentEstimate, estimateGroupsById, estimateLineItemsById],
  (est, groupsById, lineItemsById): number => {
    if (!est) return 0;
    let count = 0;
    est.group_ids.forEach((groupId: number) => {
      const group = groupsById.get(groupId);
      if (!group || group._destroy) return;
      group.line_item_ids.forEach((lineItemId: number) => {
        const lineItem = lineItemsById.get(lineItemId);
        if (lineItem && !lineItem._destroy) count += 1;
      });
    });
    return count;
  },
);

/**
 * Checks if a specific line item has unsaved changes compared to its editing snapshot.
 * Memoized — only recomputes when the snapshot maps or the line item record changes.
 */
export const isLineItemDirty = createSelector(
  [
    lineItemEditingSnapshots,
    estimateEditingSnapshots,
    (state: RootState, props: { estimateLineItemId: number }) =>
      state.getIn(["estimateLineItems", "byId", props.estimateLineItemId]),
    (_: RootState, props: { estimateLineItemId: number }) => props.estimateLineItemId,
  ],
  (snapshots, estSnapshots, current, estimateLineItemId) => {
    const hasEditingSession = (snapshots && snapshots.size > 0) || (estSnapshots && estSnapshots.size > 0);
    if (!hasEditingSession) return false;
    if (!current) return false;

    const snapshot = snapshots?.get(estimateLineItemId);
    // Positive-ID items with no snapshot are treated as dirty (conservative fallback).
    // New drafts (negative IDs) always have a snapshot stored at creation time.
    if (!snapshot) return estimateLineItemId > 0;

    return !current.equals(snapshot);
  },
);

/**
 * Estimate-level derived-total fields — pure functions of already-compared inputs (line-item
 * prices/quantities, markups, `tax_rate`, and the `discounts` list), recomputed on load by the
 * `recalculateEstimateTotals` pass dispatched from the `getEstimate` hydrate thunk. They are excluded
 * from the `isEstimateDirty` comparison because
 * they echo those inputs rather than being user-entered, and because several never round-trip through
 * the server: the estimate serializer returns `subtotal`/`total`/`tax_total` but not
 * `labor_total`/`product_total`/`discountable_total`, and `running_discountable` is a client-only
 * Record field (absent from `IEstimateData`). Left in the comparison, they diverge from the stale/zero
 * `lastSavedById` on the first post-load recalc and mark a saved, unedited estimate permanently dirty
 * (RSF-25619). `discount` and `running_discountable` are the two outputs of `calcDiscount` — both are
 * derived from the `discounts` list (the authoritative, still-compared input), never set directly;
 * excluding them matches rsf-app, which re-baselines after recomputing on load so a saved estimate is
 * never dirty from any derived total. `taxable_amount` is deliberately NOT here: it round-trips (it's
 * in the save payload) and is user-authoritative in `default_taxable_amount: "none"` mode (the Sales
 * Tax editor gates editing on `isTaxableAmountEditable`, `EstimateOverview/SalesTax.tsx`), so excluding
 * it could mask a genuine edit. Its load-time drift — and that of `payment_terms` (`calcPaymentTerms(total)`),
 * likewise not excluded — is instead handled by the `getEstimate` hydrate thunk re-baselining
 * `lastSavedById` onto the recomputed state (`copyEstimateToLastSaved` after `recalculateEstimateTotals`,
 * `common/reducers/estimate.async.actions.ts`), not by exclusion. The one exception is
 * `payment_terms.financed_amount`/`down_payment`: the hydrate thunk re-baselines just those two keys
 * from the RAW server response (`baselinePaymentTermFinancials`).
 */
export const ESTIMATE_DERIVED_TOTAL_FIELDS: Array<keyof IEstimateRecord> = [
  "subtotal",
  "total",
  "tax_total",
  "labor_total",
  "product_total",
  "discountable_total",
  "discount",
  "running_discountable",
];

/**
 * Group-level derived-total fields excluded from the `isEstimateDirty` comparison. This intentionally
 * mirrors the estimate-level set (minus the fields the group record doesn't model): the group reducer
 * recomputes all five via `sumEstimateLineItemTotals` on every recalc, but the `estimate_groups`
 * serializer round-trips only `subtotal`/`total` — so `labor_total`/`product_total`/`discountable_total`
 * hydrate to `0` on reload and drift the moment the first recalc runs, exactly like `subtotal`/`total`.
 * Excluding them cannot mask a real change: each is a pure function of line-item fields still compared
 * item-by-item below. Do NOT trim this back to `subtotal`/`total` — that reintroduces RSF-25619.
 */
export const GROUP_DERIVED_TOTAL_FIELDS: Array<keyof IEstimateGroupRecord> = [
  "subtotal",
  "total",
  "labor_total",
  "product_total",
  "discountable_total",
];

/**
 * Line-item-level derived-total fields excluded from the `isEstimateDirty` comparison. This mirrors
 * the estimate/group sets for the transient option roll-up family: the bottom-up post-load recompute
 * populates these on `byId` (via `sumEstimateLineItemOptionTotals`) but never on the server-sourced
 * `lastSavedById` (they aren't persisted — `pickLineItemFields` sends only the product-only
 * `ext_price`), so they are guaranteed to drift on a clean load and would mark a saved, unedited
 * estimate permanently dirty (RSF-25619 bug class). Excluding them cannot mask a real change: they are
 * pure echoes of already-compared inputs — the line item's own `ext_price` and its option records,
 * which stay in the comparison via full `option.equals()` (so a `cost`-option edit still trips dirty).
 * Do NOT strip anything from the options themselves.
 */
export const LINE_ITEM_DERIVED_TOTAL_FIELDS: Array<keyof IEstimateLineItemRecord> = [
  "ext_opt_price",
  "option_labor_total",
  "option_product_total",
];

/** Resets the derived-total fields to their Record defaults so they're ignored by `.equals()`. */
const stripEstimateDerivedTotals = (estimate: EstimateRecord): EstimateRecord =>
  ESTIMATE_DERIVED_TOTAL_FIELDS.reduce((acc, field) => acc.delete(field), estimate);

/** Resets the derived-total fields to their Record defaults so they're ignored by `.equals()`. */
const stripGroupDerivedTotals = (group: EstimateGroupRecord): EstimateGroupRecord =>
  GROUP_DERIVED_TOTAL_FIELDS.reduce((acc, field) => acc.delete(field), group);

/** Resets the derived-total fields to their Record defaults so they're ignored by `.equals()`. */
const stripLineItemDerivedTotals = (lineItem: EstimateLineItemRecord): EstimateLineItemRecord =>
  LINE_ITEM_DERIVED_TOTAL_FIELDS.reduce((acc, field) => acc.delete(field), lineItem);

export const ESTIMATE_FINANCE_OPTION_DERIVED_FIELDS: Array<keyof IEstimateFinanceOptionRecord> = [
  "amount",
  "monthly_payment",
  "promo_payment",
];

const stripFinanceOptionDerived = (efo: EstimateFinanceOptionRecord): EstimateFinanceOptionRecord =>
  ESTIMATE_FINANCE_OPTION_DERIVED_FIELDS.reduce((acc, field) => acc.delete(field), efo);

/**
 * Checks if the estimate has unsaved changes compared to what's saved on the server.
 * Memoized — skips the full tree traversal when no relevant state slices have changed.
 */
export const isEstimateDirty = createSelector(
  [
    byId,
    lastSavedById,
    lineItemEditingSnapshots,
    estimateGroupsById,
    estimateGroupsLastSavedById,
    estimateLineItemsById,
    estimateLineItemsLastSavedById,
    estimateLineItemOptionsById,
    estimateLineItemOptionsLastSavedById,
    estimateFinanceOptionsById,
    estimateFinanceOptionsLastSavedById,
    (_: RootState, props: { estimateId: Nullable<number> }) => props.estimateId,
  ],
  (
    currentEstimates,
    savedEstimates,
    lineItemSnapshotMap,
    currentGroups,
    savedGroups,
    currentLineItems,
    savedLineItems,
    currentOptions,
    savedOptions,
    currentFinanceOptions,
    savedFinanceOptions,
    estimateId,
  ) => {
    if (!estimateId) return false;

    const current = currentEstimates.get(estimateId);
    const saved = savedEstimates.get(estimateId);

    if (!current) return false;
    if (!saved) return false;

    const editingLineItemIds = lineItemSnapshotMap?.keySeq().toSet() || null;

    if (!stripEstimateDerivedTotals(current).equals(stripEstimateDerivedTotals(saved))) {
      return true;
    }

    for (const groupId of current.group_ids) {
      const currentGroup = currentGroups.get(groupId);
      const savedGroup = savedGroups.get(groupId);

      if (!currentGroup || !savedGroup) {
        if (currentGroup !== savedGroup) return true;
        continue;
      }

      if (
        !stripGroupDerivedTotals(currentGroup)
          .delete("line_item_ids")
          .equals(stripGroupDerivedTotals(savedGroup).delete("line_item_ids"))
      ) {
        return true;
      }

      const currentLineItemIds = editingLineItemIds
        ? currentGroup.line_item_ids.filter((id: number) => !editingLineItemIds.has(id))
        : currentGroup.line_item_ids;
      const savedLineItemIds = editingLineItemIds
        ? savedGroup.line_item_ids.filter((id: number) => !editingLineItemIds.has(id))
        : savedGroup.line_item_ids;

      if (!currentLineItemIds.equals(savedLineItemIds)) {
        return true;
      }

      for (const lineItemId of currentLineItemIds) {
        const currentLineItem = currentLineItems.get(lineItemId);
        const savedLineItem = savedLineItems.get(lineItemId);

        if (!currentLineItem || !savedLineItem) {
          if (currentLineItem !== savedLineItem) return true;
          continue;
        }

        if (!stripLineItemDerivedTotals(currentLineItem).equals(stripLineItemDerivedTotals(savedLineItem))) {
          return true;
        }

        for (const optionId of currentLineItem.option_ids) {
          const currentOption = currentOptions.get(optionId);
          const savedOption = savedOptions.get(optionId);

          if (!currentOption || !savedOption) {
            if (currentOption !== savedOption) return true;
            continue;
          }

          if (!currentOption.equals(savedOption)) {
            return true;
          }
        }
      }
    }

    for (const financeOptionId of current.finance_option_ids) {
      const currentFinanceOption = currentFinanceOptions.get(financeOptionId);
      const savedFinanceOption = savedFinanceOptions.get(financeOptionId);

      if (!currentFinanceOption) {
        continue;
      }
      if (!savedFinanceOption) {
        return true;
      }

      if (!stripFinanceOptionDerived(currentFinanceOption).equals(stripFinanceOptionDerived(savedFinanceOption))) {
        return true;
      }
    }

    return false;
  },
);

/**
 * Returns the estimate associated with the provided slide index.
 *
 * @param {RootState} state The RootState
 * @param {{slideIndex: number}} props The slide index to find the estimate for
 * @returns {Nullable<EstimateRecord>} Matching EstimateRecord for the slide index
 */
export const slideEstimate = (state: RootState, props: { slideIndex: number }) => {
  const estimateIds = state.getIn(["pricePresentation", "slideEstimateIds"]);
  return estimate(state, { estimateId: estimateIds.get(props.slideIndex) });
};
